<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col md="12" cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>

              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Budget Info
              </v-expansion-panel-title>

              <v-expansion-panel-text class="pa-3">
                <v-row>

                    <!-- Budget Type -->
                    <v-col cols="12" md="4">
                        <v-autocomplete
                        label="Budget Type"
                        v-model="budget_type_id"
                        :items="budgetTypeList"
                        item-title="name"
                        item-value="id"
                        density="compact"
                        variant="outlined"
                        clearable
                        :error-messages="errors.budget_type_id"
                        @update:model-value="changeBudgetType"
                        hide-details
                        />
                    </v-col>

                    <!-- Budget Head -->
                    <v-col cols="12" md="4">
                        <v-autocomplete
                        label="Budget Head"
                        v-model="budget_head_id"
                        :items="filteredBudgetHeadList"
                        item-title="name"
                        item-value="id"
                        density="compact"
                        variant="outlined"
                        clearable
                        :error-messages="errors.budget_head_id"
                        @update:model-value="changeBudgetHead"
                        hide-details
                        />
                    </v-col>

                    <!-- Budget Sub Head -->
                    <v-col cols="12" md="4">
                        <v-autocomplete
                        label="Budget Sub Head"
                        v-model="budget_sub_head_id"
                        :items="filteredSubHeadList"
                        item-title="name"
                        item-value="id"
                        density="compact"
                        variant="outlined"
                        clearable
                        :error-messages="errors.budget_sub_head_id"
                        hide-details
                        />
                    </v-col>

                    <!-- From Date -->
                    <v-col cols="12" md="4">
                        <v-date-input
                        label="From Date"
                        v-model="from_date"
                        density="compact"
                        variant="outlined"
                        prepend-icon=""
                        clearable
                        :error-messages="errors.from_date"
                        hide-details
                        />
                    </v-col>

                    <!-- To Date -->
                    <v-col cols="12" md="4">
                        <v-date-input
                        label="To Date"
                        v-model="to_date"
                        density="compact"
                        variant="outlined"
                        prepend-icon=""
                        clearable
                        :error-messages="errors.to_date"
                        hide-details
                        />
                    </v-col>

                    <!-- Amount -->
                    <v-col cols="12" md="4">
                        <v-text-field
                        label="Amount"
                        type="number"
                        v-model="amount"
                        density="compact"
                        variant="outlined"
                        clearable
                        :error-messages="errors.amount"
                        hide-details
                        />
                    </v-col>

                    <!-- Budget Status -->
                    <v-col cols="12" md="4">
                        <v-autocomplete
                        label="Budget Status"
                        v-model="budget_status"
                        :items="budgetStatusList"
                            item-title="title"
                            item-value="value"
                        density="compact"
                        variant="outlined"
                        clearable
                        :error-messages="errors.budget_status"
                        hide-details
                        />
                    </v-col>

                    <v-col cols="12" class="d-flex justify-end">
                        <v-btn
                            class="bg-grey-darken-1 me-2"
                            variant="flat"
                            :loading="loading"
                            @click="submitBudget(0)"
                        >
                            Draft
                        </v-btn>

                        <v-btn
                            class="bg-primary"
                            variant="flat"
                            :loading="loading"
                            @click="submitBudget(1)"
                        >
                            {{ isEdit ? "Update" : "Save" }}
                        </v-btn>
                    </v-col>

                </v-row>
              </v-expansion-panel-text>

            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>

import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import { useRouter, useRoute } from "vue-router";

const store = useStore();
const router = useRouter();
const route = useRoute();

const panel = ref([0]);

const isEdit = ref(false);
const loading = ref(false);
const budgetId = ref(null);

/* ================= FORM ================= */
const budget_type_id = ref(null);
const budget_head_id = ref(null);
const budget_sub_head_id = ref(null);
const from_date = ref(null);
const to_date = ref(null);
const amount = ref(null);
const budget_status = ref("pending");
// const status = ref(1);

/* ================= VALIDATION ================= */
const errors = ref({});

/* ================= STATIC ================= */
const budgetStatusList = [
  { title: "Approved", value: "approved" },
  { title: "Pending", value: "pending" }
];

/* ================= GETTERS ================= */
const budgetTypeList = computed(() => store.getters.getBudgetTypeList || []);
const budgetHeadList = computed(() => store.getters.getBudgetHeadList || []);
const subHeadList = computed(() => store.getters.getBudgetSubHeadList || []);

/* ================= FILTER ================= */
const filteredBudgetHeadList = computed(() => {
  return budgetHeadList.value.filter(
    (item) => item.budget_type_id == budget_type_id.value
  );
});

const filteredSubHeadList = computed(() => {
  return subHeadList.value.filter(
    (item) => item.budget_head_id == budget_head_id.value
  );
});

/* ================= METHODS ================= */
function changeBudgetType() {
  budget_head_id.value = null;
  budget_sub_head_id.value = null;
}

function changeBudgetHead() {
  budget_sub_head_id.value = null;
}

function validate() {

  errors.value = {};

  if (!budget_type_id.value) errors.value.budget_type_id = ["Required"];
  if (!budget_head_id.value) errors.value.budget_head_id = ["Required"];
  if (!budget_sub_head_id.value) errors.value.budget_sub_head_id = ["Required"];
  if (!from_date.value) errors.value.from_date = ["Required"];
  if (!to_date.value) errors.value.to_date = ["Required"];
  if (!amount.value) errors.value.amount = ["Required"];
  if (!budget_status.value) errors.value.budget_status = ["Required"];

  return Object.keys(errors.value).length === 0;
}

const submitBudget = async (statusValue) => {

  if (!validate()) return;

  loading.value = true;

  const payload = {
    id: isEdit.value ? budgetId.value : null,
    budget_sub_head_id: budget_sub_head_id.value,
    from_date: from_date.value,
    to_date: to_date.value,
    amount: amount.value,
    budget_status: budget_status.value,
    status: statusValue,
  };

  try {

    if (isEdit.value) {
      await store.dispatch("updateBudget", payload);
      toast.success("Updated successfully");
    } else {
      await store.dispatch("saveBudget", payload);
      toast.success("Saved successfully");
    }

    router.push({ name: "budget-records" });

  } catch (error) {

    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("Something went wrong!");
    }

  } finally {
    loading.value = false;
  }
};

/* ================= LOAD ================= */
const loadData = async () => {
  await store.dispatch("getBudgetTypeList");
  await store.dispatch("getBudgetHeadList");
  await store.dispatch("getBudgetSubHeadList");
};

const loadBudget = async (id) => {
  try {
    const res = await store.dispatch("getBudgetById", id);
    const data = res.data;

    budget_sub_head_id.value = data.budget_sub_head_id;
    from_date.value = data.from_date;
    to_date.value = data.to_date;
    amount.value = data.amount;
    budget_status.value = data.budget_status;

    // set head + type automatically
    budget_head_id.value = data.budget_sub_head?.budget_head_id;
    budget_type_id.value = data.budget_sub_head?.budget_head?.budget_type_id;
  } catch (e) {
    toast.error("Failed to load budget");
  }
};

onMounted(async () => {

  loading.value = true;

  await loadData();

  if (route.query.id) {
    isEdit.value = true;
    budgetId.value = route.query.id;
    await loadBudget(budgetId.value);
  }

  loading.value = false;

});
</script>